import { IBusiness, isIBusiness } from '@/coreDomainsModule/entities/businessBase';
import { OrderHeaderKey } from '../../interface/tableContentHeaders';
import { TableContentItem } from '../../interface/tableContentItems';
import { convertIDateToDateFormat, convertUnixTimeToIDate } from '../../utils/dateFormat';
import { AlertType } from '@/coreDomainsModule/entities/hasRecommendOrders';
import { BaseTableContentViewModel } from '../../baseTableContentViewModel';
import { isIDate } from '@/coreDomainsModule/date';
import { Order } from '@/coreDomainsModule/entities/order';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { PartialDelivery } from '@/coreDomainsModule/entities/partialDelivery';

/**
 * propsの定義
 * @param alertTypes - アラートの種類
 * @param deliveryDate - 納期
 * @param orderAmount - 発注数量
 */
type OrderAlertProps = {
  alertTypes: AlertType[];
  deliveryDate: string;
  orderAmount: string;
};

/**
 *　発注周りのテーブル表示に使うロジック
 */
export class OrderTableContentViewModel extends BaseTableContentViewModel {
  /**
   * 発注の項目表示用
   * @param headerKey - ヘッダーキー
   * @param item - テーブルコンテンツアイテム
   */
  public displayOrderItem = (headerKey: OrderHeaderKey, item: TableContentItem): string => {
    const useItem = item as Order; // こいつは、IUnorderedArrivalを要求しているので、ここで判定（ある前提なのでthrowErrorしてもいいかも）
    switch (headerKey) {
      case 'OrderGroupName':
        return useItem.orderGroupName ?? '--';
      case 'OrderInvoiceNumber':
        return useItem.invoiceNumber ?? '--';
      case 'OrderDestinationName':
        return useItem.destinationName ?? '--';
      case 'OrderNumber':
        return useItem.orderNumber;
      case 'OrderDesiredTimeOfArrival':
        return convertIDateToDateFormat(convertUnixTimeToIDate(useItem.desiredDeliveryDate));
      case 'OrderEstimateTimeOfArrival':
        return convertIDateToDateFormat(convertUnixTimeToIDate(useItem.estimatedTimeOfArrival));
      case 'OrderAmount':
        return useItem.orderAmount.toString();
      case 'OrderRecommendStatus':
        return useItem.orderStatus.toString();
      case 'OrderAlert':
        return '-'; // これ使わないので、とりあえずダミー
      case 'OrderMemo':
        return useItem.orderNote ?? '';
      case 'OrderDate':
        return useItem.createdOrderDate ? convertIDateToDateFormat(convertUnixTimeToIDate(useItem.createdOrderDate)) : '--';
      case 'OrderOutputDate':
        // TODO: i18n対応
        return useItem.outputDate ? DateService.convertUnixTimeToDate(useItem.outputDate / 1000).toLocaleString() : '--';
      case 'OrderLastUpdatedBy':
        return useItem.lastUpdatedByUser.name ?? '--';
      case 'OrderIssuedNumber':
        return useItem.orderIssuedNumber ?? '--';
      default:
        return '-';
    }
  };

  /**
   * 分納の項目表示用
   * @param headerKey - ヘッダーキー
   * @param item - テーブルコンテンツアイテム
   */
  public displayPartialDeliveryItem = (headerKey: OrderHeaderKey, item: TableContentItem): string => {
    const useItem = item as PartialDelivery; // こいつは、PartialDeliveryを要求しているので、ここで判定（ある前提なのでthrowErrorしてもいいかも）
    switch (headerKey) {
      case 'OrderNumber':
        return useItem.partialDeliveryNumber;
      case 'OrderEstimateTimeOfArrival':
        return convertIDateToDateFormat(convertUnixTimeToIDate(useItem.deliveryDate));
      case 'OrderAmount':
        return useItem.orderAmount.toString();
      case 'OrderRecommendStatus':
        return useItem.orderStatus.toString();
      case 'OrderLastUpdatedBy':
        return useItem.lastUpdatedByUser.name ?? '--';
      default:
        return '';
    }
  };

  /**
   * 発注のアラート表示用
   * @param item - テーブルコンテンツアイテム
   */
  public displayOrderAlert = (item: TableContentItem): OrderAlertProps => {
    // IBusiness ^ orderAlertがある時
    if (item.orderAlert) {
      const alerts = item.orderAlert;
      const deliveryDate = item?.desiredDeliveryDate;
      const orderAmount = item?.orderAmount;
      return {
        alertTypes: alerts,
        deliveryDate: deliveryDate ? convertIDateToDateFormat(convertUnixTimeToIDate(deliveryDate)) : '',
        orderAmount: orderAmount ? orderAmount.toString() : '', // TODO: 発注に必ず単位がないとおかしい？
      };
    } else {
      return {
        alertTypes: [],
        deliveryDate: '',
        orderAmount: '',
      };
    }
  };

  /**
   * 発注のアラート表示用（サンプル）
   * 上の行から決め打ちで全パターンを表示する
   * TODO: 2024/6/19 サンプル表示のために用意しているので、不要になったら削除してください
   * NOTE: 2024/12/20 複数アラート表示などで不具合があった時に処理確認など用に一旦残してます。 あとで消してください
   * @param item - テーブルコンテンツアイテム
   * @param index - インデックス
   */
  public sampleOrderAlert = (item: TableContentItem, index: number): OrderAlertProps => {
    if (index >= OrderTableContentViewModel.sampleAlertTypes.length) {
      return {
        alertTypes: [],
        deliveryDate: '',
        orderAmount: '',
      };
    }
    return {
      alertTypes: OrderTableContentViewModel.sampleAlertTypes[index],
      deliveryDate: '2024-07-01',
      orderAmount: '100',
    };
  };

  /**
   * サンプル用のアラートタイプを取得する
   * TODO: 2024/6/19 サンプル表示のために用意しているので、不要になったら削除してください
   */
  static sampleAlertTypes: AlertType[][] = (() => {
    const allAlertTypes: AlertType[] = ['OrderAmountWarning', 'DeliveryDateWarning', 'DeliveryDateInfo', 'DeliveryDateConfirmedInfo'];
    // ビット演算を使用して、indexに基づいてアラートタイプの組み合わせを生成
    const alerts: AlertType[][] = [];
    for (let i = 1; i < 16; i++) {
      const alertTypes: AlertType[] = [];
      for (let j = 0; j < allAlertTypes.length; j++) {
        if (i & (1 << j)) {
          alertTypes.push(allAlertTypes[j]);
        }
      }
      alerts.push(alertTypes);
    }
    // 見やすさのために要素数が少ない順にソート
    alerts.sort((a, b) => a.length - b.length);
    return alerts;
  })();
}
